import React, { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AjvError, FormProps, UiSchema } from '@rjsf/core';
import { MuiForm5 } from '@rjsf/material-ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { JSONSchema7 } from 'json-schema';
import * as R from 'remeda';

import { CheckboxWidget } from './components/CheckboxWidget';
import { CommandCredentialPickerField } from './components/CommandCredentialPickerField';
import { EnforceHiddenField } from './components/EnforceHiddenField';
import { FileUploadField } from './components/FileUploadField';
import { HtmlField } from './components/HtmlField';
import { ObjectFieldTemplate } from './components/ObjectFieldTemplate';
import { PcapUploadField } from './components/PcapUploadField';
import { SectionField } from './components/SectionField';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false
    }
  }
});

declare module 'react' {
  function forwardRef<TRef, TProps = Record<string, unknown>>(
    render: (props: TProps, ref: React.Ref<TRef>) => React.ReactElement | null
  ): (props: TProps & React.RefAttributes<TRef>) => React.ReactElement | null;
}

declare module '@rjsf/core' {
  interface FormProps<T> {
    ref?: Ref<T> | undefined;
  }
}

interface IAiReactJsonSchemaFormProps<TFormData> extends Omit<FormProps<TFormData>, 'onChange' | 'ref'> {
  id?: string;
  defaultFormData?: TFormData;
  setFormState?: SetFormState;
  disableOnChangeValidation?: boolean;
  onChange?: (formData: TFormData) => void;
  formDataModel?: TFormData;
  showFormSubmitButton?: boolean;
  onlyRequiredFields?: boolean;
  disableTitles?: boolean;
}

interface IFormRef<TFormData> {
  submit: () => void;
  validate: (
    formData: TFormData,
    schema: JSONSchema7
  ) => {
    errors: AjvError[];
  };
  state: {
    formData: TFormData;
    schema: JSONSchema7;
  };
}

export interface AiReactJsonSchemaFormRef {
  submit: () => void;
  validate: () => { errors: AjvError[] };
}

export type SetFormState = (formState: FormState) => void;

export type FormState = { isValid: boolean; isDirty: boolean };

export type FormDataPath = readonly (string | number)[];

export interface AiReactJsonSchemaFormContext<TFormData = unknown> {
  formData: TFormData;
  patchFormDataAtPath: (path: FormDataPath, value: unknown) => void;
}

const AiReactJsonSchemaFormComponent = <TFormData,>(
  props: IAiReactJsonSchemaFormProps<TFormData>,
  ref: Ref<AiReactJsonSchemaFormRef>
) => {
  const {
    id = 'ai-react-json-schema-form',
    onChange,
    onSubmit,
    setFormState,
    disableOnChangeValidation,
    defaultFormData = {},
    formDataModel,
    showFormSubmitButton = false,
    schema: _schema,
    onlyRequiredFields,
    disableTitles,
    uiSchema: _uiSchema,
    ...restProps
  } = props;
  const formRef = useRef<IFormRef<TFormData>>();
  const [formData, setFormData] = useState<TFormData | Record<string, unknown>>(defaultFormData);

  const effectiveFormData = formDataModel || formData;
  const formDataRef = useRef(effectiveFormData);
  useEffect(() => {
    formDataRef.current = effectiveFormData;
  });

  const schema = R.clone(_schema);
  const uiSchema = R.clone(_uiSchema) ?? {};

  injectHtmlHelpFields(schema, uiSchema);

  if (onlyRequiredFields) deleteNonRequiredProperties(schema);

  if (disableTitles) deleteTitles(schema);

  const syncFormState: FormProps<TFormData>['onChange'] = ({ formData }) => {
    formDataRef.current = formData;
    onChange?.(formData);
    setFormData(formData);

    // Serves to disable the onChange validation if we opt for onSubmit validation
    if (disableOnChangeValidation) return;

    // A bit tricky and expensive to get the form state but it is part of our current experience. Consider using onSubmit instead.
    if (formRef.current) {
      const { errors } = doValidate() || {};

      setFormState?.({ isValid: !errors?.length, isDirty: true });

      return;
    }

    // If the ref is not in place yet, the form is pristine / not dirty
    setFormState?.({ isValid: false, isDirty: false });
  };

  const patchFormDataAtPath = (path: FormDataPath, value: unknown) => {
    const next = setAtPath(formDataRef.current, path, value) as TFormData;
    formDataRef.current = next;
    onChange?.(next);
    setFormData(next);
  };

  const formContext: AiReactJsonSchemaFormContext<typeof effectiveFormData> = {
    formData: effectiveFormData,
    patchFormDataAtPath
  };

  const doSubmit = () => {
    formRef.current?.submit();
  };

  const doValidate = () => {
    return formRef.current?.validate(formRef.current.state.formData, formRef.current.state.schema) ?? { errors: [] };
  };

  useImperativeHandle(ref, () => ({
    submit: doSubmit,
    validate: doValidate
  }));

  return (
    <QueryClientProvider client={queryClient}>
      <MuiForm5
        id={id}
        formData={effectiveFormData}
        formContext={formContext}
        onSubmit={onSubmit}
        onChange={syncFormState}
        widgets={{ CheckboxWidget }}
        fields={{
          $aiSection: SectionField,
          fileupload: FileUploadField,
          pcapupload: PcapUploadField,
          html: HtmlField,
          $aiHidden: EnforceHiddenField,
          commandCredentialPicker: CommandCredentialPickerField
        }}
        ObjectFieldTemplate={ObjectFieldTemplate}
        showErrorList={false}
        schema={schema}
        uiSchema={uiSchema}
        noHtml5Validate
        {...restProps}
        ref={formRef}
      >
        {!showFormSubmitButton && <>{/* Use a fragment to avoid rendering the default submit button */}</>}
      </MuiForm5>
    </QueryClientProvider>
  );
};

const setAtPath = (obj: unknown, path: FormDataPath, value: unknown): unknown => {
  if (path.length === 0) return value;
  const [head, ...rest] = path;

  if (typeof head === 'number') {
    const source = Array.isArray(obj) ? (obj as unknown[]) : [];
    const next = source.slice();
    next[head] = setAtPath(source[head], rest, value);
    return next;
  }

  const source = obj && typeof obj === 'object' && !Array.isArray(obj) ? (obj as Record<string, unknown>) : {};
  return { ...source, [head]: setAtPath(source[head], rest, value) };
};

const injectHtmlHelpFields = (schema: JSONSchema7, uiSchema: UiSchema) => {
  schema.properties ??= {};
  const properties = schema.properties;

  Object.entries(uiSchema).forEach(([key, entry]) => {
    if (!entry || typeof entry !== 'object') return;
    if (entry['ui:widget'] !== 'html' && entry['ui:field'] !== 'html') return;

    properties[key] ??= { type: 'null' };

    const existingOptions = (entry['ui:options'] as Record<string, unknown> | undefined) ?? {};
    const inlineValue = entry['ui:value'];
    const { 'ui:widget': _widget, 'ui:value': _value, ...rest } = entry;

    uiSchema[key] = {
      ...rest,
      'ui:field': 'html',
      'ui:options': {
        ...existingOptions,
        value: existingOptions['value'] ?? inlineValue
      }
    };
  });
};

const deleteNonRequiredProperties = ({ required = [], properties = {} }: JSONSchema7) => {
  Object.entries(properties).forEach(([key, property]) => {
    if (typeof property === 'boolean') return;

    if (key === 'firedrill') delete properties[key];

    if (property.type === 'object') {
      deleteNonRequiredProperties(property);
    } else {
      if (!required.includes(key)) {
        delete properties[key];
      }
    }
  });
};

const deleteTitles = (schema: JSONSchema7) => {
  Object.values(schema.properties || {}).forEach(property => {
    if (typeof property === 'boolean') return;

    if (property.type === 'object') {
      // Empty string will prevent it from rendering the title
      property.title = '';
    } else {
      deleteTitles(property);
    }
  });
};

export const AiReactJsonSchemaForm = forwardRef(AiReactJsonSchemaFormComponent);
